// Scorecard calculation utilities
// Extracted from App.vue to eliminate duplication and improve maintainability

import { GameType } from '../enum/gameType.js';

export const ScorecardCalculations = {

    // ============================================================================
    // ITERATOR UTILITIES
    // These functions abstract the tournament/non-tournament structure differences
    // ============================================================================

    /**
     * Get all rounds from a scorecard based on filters
     * @param {Object} scorecard - The scorecard object
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly }
     * @returns {Array} Array of rounds matching the filters
     */
    getRounds(scorecard, filters = {}) {
        const { roundType, setType, lastRoundOnly } = filters;
        let rounds = [];

        if (scorecard.gameType === GameType.TOURNAMENT) {
            // Get rounds from sets
            let sets = scorecard.sets || [];

            // Filter by set type if specified
            if (setType) {
                sets = sets.filter(set => set.type === setType);
            }

            // Get last set only if requested
            if (lastRoundOnly && sets.length > 0) {
                sets = [sets[sets.length - 1]];
            }

            // Flatten all rounds from filtered sets
            rounds = sets.flatMap(set => set.rounds || []);
        } else {
            // Get rounds directly from scorecard
            rounds = scorecard.rounds || [];

            // Get last round only if requested
            if (lastRoundOnly && rounds.length > 0) {
                rounds = [rounds[rounds.length - 1]];
            }
        }

        // Filter by round type if specified
        if (roundType) {
            rounds = rounds.filter(round => round.type === roundType);
        }

        return rounds;
    },

    /**
     * Helper: Check if round should be counted (for bye calculations in round-robin)
     */
    countThis(bye, round, totalRoundsNum) {
        if (bye) {
            return round.id !== totalRoundsNum;
        }
        return true;
    },

    /**
     * Iterate through all match players for a specific player
     * @param {Object} scorecard - The scorecard object
     * @param {number} playerId - The player ID to filter for
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly, excludeRoundTypes }
     * @returns {Array} Array of {matchPlayer, round, match} objects
     */
    iterateMatchPlayers(scorecard, playerId, filters = {}) {
        const { excludeRoundTypes = [] } = filters;
        const rounds = this.getRounds(scorecard, filters);
        const results = [];
        const totalRoundsNum = scorecard.rounds?.length;

        rounds.forEach(round => {
            // Skip excluded round types
            if (excludeRoundTypes.includes(round.type)) {
                return;
            }

            round.matches?.forEach(match => {
                match.matchPlayers?.forEach(matchPlayer => {
                    const matchPlayerId = matchPlayer.player?.id || matchPlayer.id;

                    if (matchPlayerId === playerId) {
                        // Check if this round should be counted (bye handling)
                        const shouldCount = this.countThis(matchPlayer.bye, round, totalRoundsNum);

                        if (shouldCount) {
                            results.push({ matchPlayer, round, match });
                        }
                    }
                });
            });
        });

        return results;
    },

    /**
     * Iterate through all throws for a specific player
     * @param {Object} scorecard - The scorecard object
     * @param {number} playerId - The player ID to filter for
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly, excludeRoundTypes, scoreFilter }
     * @returns {Array} Array of {throw, matchPlayer, round, match} objects
     */
    iterateThrows(scorecard, playerId, filters = {}) {
        const { scoreFilter, scoredOnly = false } = filters;
        const matchPlayers = this.iterateMatchPlayers(scorecard, playerId, filters);
        const results = [];

        matchPlayers.forEach(({ matchPlayer, round, match }) => {
            matchPlayer.throws?.forEach(pThrow => {
                // Apply score filter if specified
                if (scoreFilter !== undefined) {
                    const score = parseInt(pThrow.basicScore);
                    if (score !== scoreFilter && pThrow.basicScore !== scoreFilter) {
                        return;
                    }
                }

                // Apply scored filter if specified
                if (scoredOnly && !pThrow.scored) {
                    return;
                }

                results.push({
                    throw: pThrow,
                    matchPlayer,
                    round,
                    match
                });
            });
        });

        return results;
    },

    // ============================================================================
    // CALCULATION METHODS
    // ============================================================================

    /**
     * Calculate total throws for a specific round
     * @deprecated Use calculateTotalThrows with filters instead
     */
    calculateTotalThrowsForRound(player, round, totalRoundsNum) {
        let total = 0;
        const playerId = player.id;

        if (round.type === "Practice" || round.type === "Finals") {
            return 0;
        }

        for (let y = 0; y < round.matches.length; y++) {
            const match = round.matches[y];
            for (let z = 0; z < match.matchPlayers.length; z++) {
                const matchPlayer = match.matchPlayers[z];
                const matchPlayerId = matchPlayer.player.id;

                if (matchPlayerId === playerId) {
                    const countThis = this.countThis(matchPlayer.bye, round, totalRoundsNum);
                    if (countThis) {
                        for (let a = 0; a < matchPlayer.throws.length; a++) {
                            const pThrow = matchPlayer.throws[a];
                            total += parseInt(pThrow.basicScore);
                        }
                    }
                }
            }
        }
        return total;
    },

    /**
     * Calculate total throws across all rounds
     * @param {Object} player - The player object
     * @param {Object} scorecard - The scorecard object
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly }
     * @returns {number} Total throw value
     */
    calculateTotalThrows(player, scorecard, filters = {}) {
        // Default behavior: exclude Practice and Finals
        const defaultFilters = {
            excludeRoundTypes: ['Practice', 'Finals'],
            ...filters
        };

        const throws = this.iterateThrows(scorecard, player.id, defaultFilters);
        return throws.reduce((sum, { throw: pThrow }) => sum + parseInt(pThrow.basicScore), 0);
    },

    /**
     * Calculate bullseyes for a specific round
     * @deprecated Use calculateTotalBullseyes with filters instead
     */
    calculateBullseyesForRound(player, round, totalNumRounds) {
        let total = 0;

        for (let y = 0; y < round.matches.length; y++) {
            const match = round.matches[y];
            for (let z = 0; z < match.matchPlayers.length; z++) {
                const matchPlayer = match.matchPlayers[z];
                const matchPlayerId = matchPlayer.player.id;

                if (matchPlayerId === player.id) {
                    const countThis = this.countThis(matchPlayer.bye, round, totalNumRounds);
                    if (countThis) {
                        for (let a = 0; a < matchPlayer.throws.length; a++) {
                            if (matchPlayer.throws[a].basicScore === "5" || matchPlayer.throws[a].basicScore === 5) {
                                total++;
                            }
                        }
                    }
                }
            }
        }
        return total;
    },

    /**
     * Calculate total bullseyes
     * @param {Object} player - The player object
     * @param {Object} scorecard - The scorecard object
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly }
     * @returns {number} Total bullseyes count
     */
    calculateTotalBullseyes(player, scorecard, filters = {}) {
        // Apply default filter to exclude Finals unless explicitly requesting them
        const defaultFilters = {
            excludeRoundTypes: filters.roundType === 'Finals' ? [] : ['Finals'],
            ...filters
        };

        const throws = this.iterateThrows(scorecard, player.id, {
            ...defaultFilters,
            scoreFilter: 5
        });

        return throws.length;
    },

    /**
     * Calculate total bullseyes in finals
     * @deprecated Use calculateTotalBullseyes with filters: { roundType: 'Finals' }
     */
    calculateTotalBullseyesFinals(player, scorecard, lastRoundOnly = false) {
        return this.calculateTotalBullseyes(player, scorecard, {
            roundType: 'Finals',
            lastRoundOnly
        });
    },

    /**
     * Calculate total wins
     * @param {Object} player - The player object
     * @param {Object} scorecard - The scorecard object
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly }
     * @returns {number} Total wins count
     */
    calculateTotalWins(player, scorecard, filters = {}) {
        // Default behavior: exclude Finals unless explicitly requesting them
        const defaultFilters = {
            excludeRoundTypes: filters.roundType === 'Finals' ? [] : ['Finals'],
            ...filters
        };

        const matchPlayers = this.iterateMatchPlayers(scorecard, player.id, defaultFilters);
        return matchPlayers.filter(({ matchPlayer }) => matchPlayer.winner).length;
    },

    /**
     * Calculate total wins in finals
     * @deprecated Use calculateTotalWins with filters: { roundType: 'Finals' }
     */
    calculateTotalWinsFinals(player, scorecard, lastRoundOnly = false) {
        return this.calculateTotalWins(player, scorecard, {
            roundType: 'Finals',
            lastRoundOnly
        });
    },

    /**
     * Calculate total losses
     */
    calculateTotalLoss(player, scorecard) {
        const matchPlayers = this.iterateMatchPlayers(scorecard, player.id, {});
        return matchPlayers.filter(({ matchPlayer }) => !matchPlayer.winner).length;
    },

    /**
     * Calculate total games played
     */
    calculateTotalGP(player, scorecard) {
        const matchPlayers = this.iterateMatchPlayers(scorecard, player.id, {});
        return matchPlayers.length;
    },

    /**
     * Calculate throw count for a round
     * @deprecated Use calculateThrows with filters instead
     */
    calculateThrowsForRound(player, round, totalNumRounds) {
        let total = 0;

        if (round.type === "Finals" || round.type === "Practice") {
            return 0;
        }

        for (let y = 0; y < round.matches.length; y++) {
            const match = round.matches[y];
            for (let z = 0; z < match.matchPlayers.length; z++) {
                const matchPlayer = match.matchPlayers[z];
                const matchPlayerId = matchPlayer.player.id;

                if (matchPlayerId === player.id) {
                    const countThis = this.countThis(matchPlayer.bye, round, totalNumRounds);
                    if (countThis) {
                        total += matchPlayer.throws.length;
                    }
                }
            }
        }
        return total;
    },

    /**
     * Calculate total throws count
     * @param {Object} player - The player object
     * @param {Object} scorecard - The scorecard object
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly }
     * @returns {number} Total number of throws
     */
    calculateThrows(player, scorecard, filters = {}) {
        // Default behavior: exclude Finals and Practice
        const defaultFilters = {
            excludeRoundTypes: filters.roundType === 'Finals' ? [] : ['Finals', 'Practice'],
            ...filters
        };

        const throws = this.iterateThrows(scorecard, player.id, defaultFilters);
        return throws.length;
    },

    /**
     * Calculate throws count in finals
     * @deprecated Use calculateThrows with filters: { roundType: 'Finals' }
     */
    calculateThrowsFinals(player, scorecard) {
        return this.calculateThrows(player, scorecard, {
            roundType: 'Finals'
        });
    },

    /**
     * Calculate total throws value in finals
     * @deprecated Use calculateTotalThrows with filters: { roundType: 'Finals' }
     */
    calculateTotalThrowsFinals(player, scorecard, lastRoundOnly = false) {
        return this.calculateTotalThrows(player, scorecard, {
            roundType: 'Finals',
            lastRoundOnly,
            excludeRoundTypes: [] // Override default exclusions
        });
    },

    /**
     * Calculate axe hit count for specific score on a round
     * @deprecated Use calculateAxeHit with round-specific logic
     */
    calculateAxeHitOnRound(playerId, round, basicScore) {
        const calculateThisRound = !(round.title !== undefined && round.title === 'Practice');
        let total = 0;

        if (calculateThisRound) {
            for (let y = 0; y < round.matches.length; y++) {
                const match = round.matches[y];
                for (let z = 0; z < match.matchPlayers.length; z++) {
                    const matchPlayer = match.matchPlayers[z];
                    const matchPlayerId = matchPlayer.player.id;

                    if (matchPlayerId === playerId) {
                        for (let a = 0; a < matchPlayer.throws.length; a++) {
                            const pThrow = matchPlayer.throws[a];
                            const bScore = parseInt(pThrow.basicScore);
                            if (bScore === basicScore) {
                                total++;
                            }
                        }
                    }
                }
            }
        }
        return total;
    },

    /**
     * Calculate axe hit count for specific score across scorecard
     */
    calculateAxeHit(player, scorecard, basicScore) {
        const parsedBasicScore = parseInt(basicScore);

        // Exclude Practice rounds
        const throws = this.iterateThrows(scorecard, player.id, {
            scoreFilter: parsedBasicScore,
            excludeRoundTypes: ['Practice']
        });

        return throws.length;
    },

    /**
     * Calculate average throw for a round
     * @deprecated Use calculateAverageThrow with filters instead
     */
    calculateAverageThrowForRound(mPlayer, round, gameType, totalRoundsNum = null) {
        let total = 0;
        let throws = 0;
        const playerId = mPlayer.player?.id || mPlayer.id;

        if (round.type === "Practice" || round.type === "Finals") {
            return { total: 0, throws: 0 };
        }

        for (let y = 0; y < round.matches.length; y++) {
            const match = round.matches[y];
            for (let z = 0; z < match.matchPlayers.length; z++) {
                const matchPlayer = match.matchPlayers[z];
                const matchPlayerId = matchPlayer.player.id;

                if (matchPlayerId === playerId) {
                    if (gameType === GameType.TOURNAMENT) {
                        for (let a = 0; a < matchPlayer.throws.length; a++) {
                            const pThrow = matchPlayer.throws[a];
                            if (pThrow.scored) {
                                total += parseInt(pThrow.basicScore);
                                throws++;
                            }
                        }
                    } else {
                        const countThis = this.countThis(matchPlayer.bye, round, totalRoundsNum);
                        if (countThis) {
                            for (let a = 0; a < matchPlayer.throws.length; a++) {
                                const pThrow = matchPlayer.throws[a];
                                total += parseInt(pThrow.basicScore);
                                throws++;
                            }
                        }
                    }
                }
            }
        }
        return { total, throws };
    },

    /**
     * Calculate average throw across scorecard
     * @param {Object} player - The player object
     * @param {Object} scorecard - The scorecard object
     * @param {Object} filters - Optional filters: { roundType, setType, lastRoundOnly }
     * @returns {number} Average throw value rounded to 2 decimal places
     */
    calculateAverageThrow(player, scorecard, filters = {}) {
        // Default behavior: exclude Finals and Practice
        const defaultFilters = {
            excludeRoundTypes: filters.roundType === 'Finals' ? [] : ['Finals', 'Practice'],
            scoredOnly: true,
            ...filters
        };

        const throws = this.iterateThrows(scorecard, player.id, defaultFilters);

        if (throws.length === 0) return 0;

        const total = throws.reduce((sum, { throw: pThrow }) => {
            return sum + parseInt(pThrow.basicScore);
        }, 0);

        const average = total / throws.length;
        return Math.round(average * 100) / 100;
    },

    /**
     * Calculate average throw in finals
     * @deprecated Use calculateAverageThrow with filters: { roundType: 'Finals' }
     */
    calculateAverageThrowFinals(player, scorecard, lastRoundOnly = false) {
        return this.calculateAverageThrow(player, scorecard, {
            roundType: 'Finals',
            lastRoundOnly,
            excludeRoundTypes: [] // Override default exclusions
        });
    },

    /**
     * Calculate total throws for a match
     */
    calculateTotalThrowsMatch(throws) {
        return throws.reduce((sum, pThrow) => sum + parseInt(pThrow.basicScore), 0);
    },

    /**
     * Calculate total bullseyes for a match
     */
    calculateTotalBullseyesMatch(throws) {
        return throws.reduce((sum, pThrow) => {
            const score = parseInt(pThrow.basicScore);
            return score === 5 ? sum + 1 : sum;
        }, 0);
    },

    /**
     * Calculate throw points distribution for chart
     */
    calculateThrowPoints(player, gameData) {
        const arr = [];

        if (gameData && gameData.rounds != null) {
            for (let i = 0; i <= 5; i++) {
                arr.push({
                    label: i.toString(),
                    value: this.calculateAxeHit(player, gameData, i)
                });
            }
        } else {
            for (let i = 0; i <= 5; i++) {
                arr.push({ label: i.toString(), value: 0 });
            }
        }
        return arr;
    }
};
